import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit, Search, List, Table2 } from "lucide-react";
import { useLevelBrowserPolicies } from "@/hooks/useLevelBrowserPolicies";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import { useToast } from "@/hooks/use-toast";
import { CategoryAccordion } from "@/components/CategoryAccordion";
import { ALL_SUBCATEGORY_NAMES } from "@/config/categoryHierarchy";

interface LevelBrowserCategoriesContentProps {
  levelId: string;
}

type ViewMode = 'accordion' | 'table';

export const LevelBrowserCategoriesContent = ({ levelId }: LevelBrowserCategoriesContentProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<ViewMode>('accordion');
  const [searchQuery, setSearchQuery] = useState('');
  const { categories } = useWebsiteCategories();
  const { browserPolicy, updateBrowserPolicy } = useLevelBrowserPolicies(levelId);
  const { toast } = useToast();

  // Get blocked categories from policy
  const blockedCategories = useMemo(() => {
    return browserPolicy?.categories || [];
  }, [browserPolicy]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = ALL_SUBCATEGORY_NAMES.length;
    const blocked = blockedCategories.length;
    const allowed = total - blocked;
    return { total, blocked, allowed };
  }, [blockedCategories]);

  const getCategoryStatus = (categoryName: string) => {
    // Check if category is in the browser policy's categories array
    // Categories in the array are blocked, not in array are allowed
    if (browserPolicy?.categories.includes(categoryName)) {
      return 'Blocked';
    }
    return 'Allowed';
  };

  const getRowBackgroundColor = (status: string) => {
    if (status === 'Allowed') {
      return 'bg-green-50 hover:bg-green-100';
    } else if (status === 'Blocked') {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };

  const handleStatusChangeLocal = (categoryName: string, newStatus: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [categoryName]: newStatus
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Build the new categories array based on pending changes
      // Categories array now contains blocked categories
      const newBlockedCategories = categories
        .filter(category => {
          const pendingStatus = pendingChanges[category.name];
          if (pendingStatus) {
            return pendingStatus === 'blocked';
          }
          // If no pending change, keep current status
          return browserPolicy?.categories.includes(category.name);
        })
        .map(category => category.name);

      await updateBrowserPolicy({ categories: newBlockedCategories });
      setPendingChanges({});
      setIsEditMode(false);
      toast({
        title: "Changes Saved",
        description: "Category settings have been updated."
      });
    } catch (error) {
      console.error('Failed to save category changes:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelChanges = () => {
    setPendingChanges({});
    setIsEditMode(false);
  };

  const handleAllowAll = async () => {
    try {
      // Allow all categories by setting categories array to empty
      await updateBrowserPolicy({ categories: [] });
      toast({
        title: "All Categories Allowed",
        description: "All website categories have been allowed for this level."
      });
    } catch (error) {
      console.error('Failed to allow all categories:', error);
      toast({
        title: "Error",
        description: "Failed to allow all categories. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleBlockAll = async () => {
    try {
      // Block all categories using hierarchy names
      await updateBrowserPolicy({ categories: [...ALL_SUBCATEGORY_NAMES] });
      toast({
        title: "All Categories Blocked",
        description: "All website categories have been blocked for this level."
      });
    } catch (error) {
      console.error('Failed to block all categories:', error);
      toast({
        title: "Error",
        description: "Failed to block all categories. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handler for accordion view changes
  const handleAccordionCategoriesChange = async (newBlockedCategories: string[]) => {
    try {
      await updateBrowserPolicy({ categories: newBlockedCategories });
      toast({
        title: "Changes Saved",
        description: "Category settings have been updated."
      });
    } catch (error) {
      console.error('Error updating categories:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Filter categories for table view
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat => cat.name.toLowerCase().includes(query));
  }, [categories, searchQuery]);

  if (!browserPolicy) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Website Categories</h3>
          <p className="text-sm text-gray-600">Manage website category permissions for this level</p>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="flex items-center gap-4 text-sm">
        <span>
          <span className="font-medium text-green-600">{stats.allowed}</span>
          <span className="text-muted-foreground ml-1">Allowed</span>
        </span>
        <span>
          <span className="font-medium text-red-600">{stats.blocked}</span>
          <span className="text-muted-foreground ml-1">Blocked</span>
        </span>
        <span>
          <span className="font-medium">{stats.total}</span>
          <span className="text-muted-foreground ml-1">Total</span>
        </span>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={viewMode === 'accordion' ? 'default' : 'outline'}
            onClick={() => setViewMode('accordion')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'outline'}
            onClick={() => setViewMode('table')}
          >
            <Table2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {!isEditMode && (
          <>
            <Button variant="outline" size="sm" onClick={handleAllowAll}>
              Allow All
            </Button>
            <Button variant="outline" size="sm" onClick={handleBlockAll}>
              Block All
            </Button>
          </>
        )}
        {isEditMode ? (
          <>
            <Button variant="outline" size="sm" onClick={handleCancelChanges}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSaveChanges} disabled={Object.keys(pendingChanges).length === 0}>
              Save Changes
            </Button>
          </>
        ) : viewMode === 'table' ? (
          <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        ) : null}
      </div>

      {/* Accordion View */}
      {viewMode === 'accordion' && (
        <CategoryAccordion
          blockedCategories={blockedCategories}
          onCategoriesChange={handleAccordionCategoriesChange}
          searchQuery={searchQuery}
        />
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCategories
                  .sort((a, b) => {
                    const statusA = getCategoryStatus(a.name);
                    const statusB = getCategoryStatus(b.name);
                    
                    // First sort by status (Allowed first, then Blocked)
                    if (statusA !== statusB) {
                      return statusA === 'Allowed' ? -1 : 1;
                    }
                    
                    // Then sort alphabetically by name within the same status
                    return a.name.localeCompare(b.name);
                  })
                  .map((category) => {
                  const status = getCategoryStatus(category.name);
                  const pendingStatus = pendingChanges[category.name];
                  const currentStatus = pendingStatus ? (pendingStatus === 'allowed' ? 'Allowed' : 'Blocked') : status;
                  
                  return (
                    <TableRow key={category.id} className={`${getRowBackgroundColor(currentStatus)} animate-fade-in`}>
                      <TableCell>
                        <div className="font-medium">{category.name}</div>
                      </TableCell>
                      <TableCell>
                        {isEditMode ? (
                           <Select 
                            value={pendingChanges[category.name] || (status === 'Allowed' ? 'allowed' : 'blocked')} 
                            onValueChange={value => handleStatusChangeLocal(category.name, value)}
                          >
                            <SelectTrigger className="w-64">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50">
                              <SelectItem value="allowed">
                                <div className="flex items-center justify-between w-full">
                                  <span>Allowed</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="blocked">
                                <div className="flex items-center justify-between w-full">
                                  <span>Blocked</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge variant={currentStatus === 'Allowed' ? 'success' : 'destructive'}>
                              {currentStatus}
                            </Badge>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {isEditMode && viewMode === 'table' && (
        <div className="text-xs text-gray-500 p-3 bg-blue-50 rounded-lg">
          <p><strong>Edit Mode:</strong> Use the dropdowns to change category permissions for this level. 
          Click "Save Changes" to apply your changes or "Cancel" to discard them.</p>
        </div>
      )}
    </div>
  );
};
